import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../../comun/services/grpc/mapConnectError";
import { createCachedFactory } from "../../../../utils/factoryUtils";
import {
  MonitoreoUsuarioItem,
  MonitoreoUsuarioService,
} from "../../../../generated/grpc/configuracion_monitoreo_pb";
import { grpcMonitoreoUsuarioDatabase } from "../database/GrpcMonitoreoUsuarioDatabase";
import { serviceSync } from "../../../../base/services/ServiceSync";
import { MonitoreoUsuarioResponse } from "../response/MonitoreoUsuarioResponse";

/** Variante gRPC-Web (con delta real, por cliente) de
 *  `MonitoreoUsuarioGetListByClienteIdService` (ver ese archivo). Devuelve
 *  `MonitoreoUsuarioResponse[]` -mismo shape que ya usa el resto de la app-. */
class MonitoreoUsuarioGrpcService {
  constructor(private readonly _clienteId: string) {}

  async getListAsync(): Promise<MonitoreoUsuarioResponse[]> {
    await this._syncAsync();
    const items = await grpcMonitoreoUsuarioDatabase.getListByClienteIdAsync<MonitoreoUsuarioItem>(
      this._clienteId,
    );
    return items.map(toMonitoreoUsuarioResponse);
  }

  clearCache(): void {
    serviceSync.reset(`grpc:monitoreo-usuarios:${this._clienteId}`);
  }

  private async _syncAsync(): Promise<void> {
    await serviceSync.run(`grpc:monitoreo-usuarios:${this._clienteId}`, async () => {
      const ultimaSincronizacion = await grpcMonitoreoUsuarioDatabase.getUltimaSincronizacionAsync(
        this._clienteId,
      );
      const client = createClient(MonitoreoUsuarioService, grpcTransportProvider.get());

      let response;
      try {
        response = await client.listByClienteId({
          clienteId: this._clienteId,
          ultimaSincronizacion,
        });
      } catch (error) {
        throw mapConnectError(error);
      }

      await grpcMonitoreoUsuarioDatabase.putDeltaAsync(
        this._clienteId,
        response.items,
        response.timestampSincronizacion,
      );
    });
  }
}

/** Factory cacheada: una única instancia por clienteId (comparte cooldown/caché,
 *  mismo criterio que `fx_createMonitoreoUsuarioGetListByClienteIdService`). */
export const fx_createMonitoreoUsuarioGrpcService = createCachedFactory(
  (clienteId: string) => new MonitoreoUsuarioGrpcService(clienteId),
);

/** Adapta un `MonitoreoUsuarioItem` (gRPC) al shape `MonitoreoUsuarioResponse`
 *  (REST). Exportada porque el servicio de guardado/acciones también la
 *  necesita. */
export function toMonitoreoUsuarioResponse(
  usuario: MonitoreoUsuarioItem,
): MonitoreoUsuarioResponse {
  return {
    id: usuario.id,
    isEliminado: usuario.eliminado,
    clienteId: usuario.clienteId,
    nombre: usuario.nombre,
    email: usuario.email,
    pin: usuario.pin,
    ultimoIngreso: usuario.ultimoIngreso ?? null,
    isActivo: usuario.isActivo,
  };
}
